import type { SequenceMarking, SequenceOptions } from './zone-math';

export const NO_VALID_ANCHOR = 'TIADA SEQUENCE ANCHOR VALID';

// FIBO SEQUENCES MARKING. The second 1 has both predecessors (0, 1).
// Wave 1 = MIN, Wave 2 = MAX. Rank by the symmetric validation ratio.
// Ascending sequence order keeps the smaller anchor when scores are equal.
export function fiboSequenceMarking(input: number, options: SequenceOptions = {}): SequenceMarking {
  if (!Number.isFinite(input) || input <= 0 || input > Number.MAX_SAFE_INTEGER) {
    return { valid: false, error: NO_VALID_ANCHOR };
  }
  const fibo = [0, 1, 1];
  while (fibo[fibo.length - 1] < input) {
    const next = fibo[fibo.length - 1] + fibo[fibo.length - 2];
    // All returned Fibonacci integers must remain exact in JavaScript.
    // At this boundary the last safe anchor is the best supported candidate.
    if (!Number.isSafeInteger(next)) break;
    fibo.push(next);
  }
  let bestIndex = -1, bestValidation = 0;
  let nearestIndex = -1, nearestValidation = -1;
  // Owner-approved exception (13 Oct 2026): 483 pips and the supplied
  // 484.7-pip screenshot (CASE 484) use 610 -> MIN 233 / MAX 377.
  // Explicit chart context is required; standalone calculations stay >=80%.
  const xauException = options.xauPipException && (input === 483 || input === 484);
  for (let index = 2; index < fibo.length; index++) {
    const anchor = fibo[index];
    const validation = (Math.min(input, anchor) / Math.max(input, anchor)) * 100;
    if (validation > nearestValidation) {
      nearestIndex = index;
      nearestValidation = validation;
    }
    // Validate the real ratio first; rounding is for display only.
    if ((validation >= 80 || (xauException && anchor === 610)) && validation > bestValidation) {
      bestIndex = index;
      bestValidation = validation;
    }
  }
  // Owner request (5 Nov 2026): completed Z/P must not silently lose Wave
  // 1/2 in validation gaps (e.g. CASE 114). Only chart projections opt in.
  // Preserve every existing valid result and the explicit XAU 483 exception.
  const chartFallback = bestIndex < 0 && options.chartWaveFallback && nearestIndex >= 0;
  if (chartFallback) {
    bestIndex = nearestIndex;
    bestValidation = nearestValidation;
  }
  if (bestIndex < 0) return { valid: false, error: NO_VALID_ANCHOR };
  const anchor = fibo[bestIndex];
  return { valid: true, case: input, sequence_anchor: anchor,
    min: fibo[bestIndex - 2], max: fibo[bestIndex - 1], equal: anchor,
    validation: (bestValidation + Number.EPSILON * 100).toFixed(2) + '%',
    ...(bestValidation < 80 ? { validationException: chartFallback ? 'chart-nearest-anchor' as const : 'xau-483-pips' as const } : {}) };
}

export function formatSequenceMarking(result: SequenceMarking) {
  if (!result.valid) return NO_VALID_ANCHOR;
  return [
    'CASE: ' + result.case,
    '',
    '1. SEQUENCE ANCHOR: ' + result.sequence_anchor,
    '',
    '2. MIN / MAX / EQUAL',
    'MIN: ' + result.min,
    'MAX: ' + result.max,
    'EQUAL: ' + result.equal,
    '',
    '3. VALIDATION',
    '(' + Math.min(result.case!, result.sequence_anchor!) + ' ÷ ' + Math.max(result.case!, result.sequence_anchor!) + ') × 100 = ' + result.validation + (result.validationException === 'chart-nearest-anchor' ? ' · WAVE CHART (BAWAH 80%)' : result.validationException ? ' ✅ VALID (PELEPASAN XAUUSD)' : ' ✅ VALID'),
  ].join('\n');
}
